// Startprojekt für die Entwicklung: eine Session mit Spuren, Akkorden und einer Hook als MIDI.
import type { Project, Track, Clip, Note } from '@srs/shared';

const now = () => new Date().toISOString();
const by = (userId: string, ai = false) => ({ userId, ai, at: now() });
let n = 0;
export const uid = (p = 'id') => `${p}-${Date.now().toString(36)}-${(n++).toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

export function emptyTrack(name: string, kind: Track['kind'], color: string, extra: Partial<Track> = {}): Track {
  return { id: uid('t'), name, kind, color, armed: false, mute: false, solo: false, volumeDb: 0, pan: 0, inserts: [], sends: [], ...extra };
}

export function demoProject(userId: string): Project {
  const bpm = 124, spb = 60 / bpm;
  const tracks: Track[] = [
    emptyTrack('Kick', 'midi', '#003399', { instrument: { plugin: 'srs:drum' } }),
    emptyTrack('Snare', 'midi', '#2f5fd0', { instrument: { plugin: 'srs:drum' } }),
    emptyTrack('Bass', 'midi', '#0f8b8d', { instrument: { plugin: 'srs:synth', preset: 'bass' } }),
    emptyTrack('Rhodes', 'midi', '#6b4fbb', { instrument: { plugin: 'srs:synth', preset: 'keys' }, pan: -0.3 }),
    emptyTrack('Hook', 'midi', '#c7861b', { instrument: { plugin: 'srs:synth', preset: 'lead' } }),
    emptyTrack('Lead Vox', 'audio', '#e23e2c', { input: { kind: 'browser' } }),
  ];
  const [kick, snare, bass, keys, hook] = tracks;
  const bars = 8;
  const mk = (track: Track, notes: Omit<Note, 'by'>[], name: string): Clip => ({
    id: uid('c'), trackId: track.id, kind: 'midi', name, start: 0, offset: 0, duration: bars * 4 * spb, fadeIn: 0, fadeOut: 0,
    notes: notes.map((x) => ({ ...x, by: by(userId) })), by: by(userId),
  });
  const kickN = [], snareN = [], bassN = [], keysN: Omit<Note, 'by'>[] = [];
  const prog = [[54, 57, 61, 64], [50, 54, 57, 61], [45, 49, 52, 56], [52, 56, 59, 63]]; // F#m9 · D · A · E (vereinfacht)
  const roots = [42, 38, 45, 40];
  for (let b = 0; b < bars; b++) {
    for (let q = 0; q < 4; q++) kickN.push({ t: b * 4 + q, d: 0.25, m: 36, v: 0.95 });
    snareN.push({ t: b * 4 + 1, d: 0.25, m: 38, v: 0.85 }, { t: b * 4 + 3, d: 0.25, m: 38, v: 0.85 });
    const r = roots[(b >> 1) % 4];
    for (let e = 0; e < 8; e++) bassN.push({ t: b * 4 + e * 0.5, d: 0.4, m: e % 4 === 3 ? r + 12 : r, v: e % 2 ? 0.6 : 0.85 });
    if (b % 2 === 0) for (const m of prog[(b >> 1) % 4]) keysN.push({ t: b * 4, d: 7.5, m, v: 0.55 });
  }
  const hookN = [[0, 0.5, 66], [0.5, 0.5, 68], [1, 1, 69], [2, 0.5, 68], [2.5, 0.5, 66], [3, 1, 64]].map(([t, d, m]) => ({ t: t + 16, d, m, v: 0.8 }));
  return {
    id: 'demo-neon-hearts', name: 'Neon Hearts', bpm, key: 'F#-Moll', beatsPerBar: 4, sampleRate: 48000,
    tracks,
    clips: [mk(kick, kickN, 'Kick'), mk(snare, snareN, 'Snare'), mk(bass, bassN, 'Bass'), mk(keys, keysN, 'Rhodes'), { ...mk(hook, hookN, 'Hook (gepfiffen)'), start: 0 }],
    takes: [], comps: {},
    markers: [{ id: uid('m'), name: 'Strophe', time: 0 }, { id: uid('m'), name: 'Pre', time: 2 * 4 * spb }, { id: uid('m'), name: 'Refrain', time: 4 * 4 * spb }],
    loop: { enabled: false, start: 0, end: bars * 4 * spb },
    chords: [0, 2, 4, 6].map((b, i) => ({ time: b * 4 * spb, symbol: ['F#m9', 'Dmaj7', 'A', 'E'][i] })),
    lyrics: [],
    brief: { client: 'Künstler B, Sommer-Single', text: 'Gitarre im Vordergrund, Pfeif-Hook willkommen, 118 bis 126 BPM, unter 3:15, Hook vor 0:30.', bpmMin: 118, bpmMax: 126, maxSeconds: 195, hookBySeconds: 30 },
    masterDb: 0, version: 1, updatedAt: now(),
  };
}

/** Neuer, leerer Song mit Grundspuren */
export function emptyProject(name: string, bpm = 120, key = 'C-Dur'): Project {
  const spb = 60 / bpm;
  return {
    id: uid('p'), name, bpm, key, beatsPerBar: 4, sampleRate: 48000,
    tracks: [
      emptyTrack('Kick', 'midi', '#003399', { instrument: { plugin: 'srs:drum' } }),
      emptyTrack('Snare', 'midi', '#2f5fd0', { instrument: { plugin: 'srs:drum' } }),
      emptyTrack('Bass', 'midi', '#0f8b8d', { instrument: { plugin: 'srs:synth', preset: 'bass' } }),
      emptyTrack('Keys', 'midi', '#6b4fbb', { instrument: { plugin: 'srs:synth', preset: 'keys' } }),
      emptyTrack('Hook', 'midi', '#c7861b', { instrument: { plugin: 'srs:synth', preset: 'lead' } }),
      emptyTrack('Lead Vox', 'audio', '#e23e2c', { input: { kind: 'browser' } }),
    ],
    clips: [], takes: [], comps: {}, markers: [{ id: uid('m'), name: 'Strophe', time: 0 }], loop: { enabled: false, start: 0, end: 16 * spb },
    chords: [], lyrics: [], brief: { client: '', text: '' }, masterDb: 0, version: 1, updatedAt: new Date().toISOString(),
  };
}
